import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { useAppBranding } from '../../../config/branding';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import dashboard from '../../../utils/api/dashboard';

interface PenaltyRevenueChartProperties {
  dateDuration: string;
  filters?: {
    facilityIds: string[];
    stationIds: string[];
    deviceIds: string[];
    type: string[];
  };
}

/**
 * PenaltyRevenueChart component
 * Visualizes penalty revenue over time using a smooth area chart
 */
function PenaltyRevenueChart({ dateDuration, filters }: Readonly<PenaltyRevenueChartProperties>) {
  console.log(filters, 'filters');
  const branding = useAppBranding();
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  /**
   * Handles navigation to the penalty management overrides page.
   */
  const handleNavigate = () => {
    navigate('../penalty-management/overrides');
  };
  /**
   * Fetches penalty revenue data from the API and updates chart state.
   */
  const fetchPenaltyRevenue = async () => {
    setIsLoading(true);
    try {
      const { type, ...otherFilters } = filters || {};
      const response = await dashboard.getPenaltyRevenue({
        timePeriod: dateDuration,
        ...otherFilters,
        type,
      });
      if (response?.status === 200 && Array.isArray(response?.data)) {
        const labels = response.data.map((item: any) => item.label);
        const penaltyRevenueData = response.data.map((item: any) => item.penaltyRevenue);

        setChartData({
          labels,
          datasets: [
            {
              label: t('penaltyRevenue'),
              data: penaltyRevenueData,
              fill: true,
              borderColor: '#F79009',
              /**
               * Sets the background color using a linear gradient.
               * @param context - The chart context.
               * @returns The linear gradient object or null.
               */
              backgroundColor: (context: any) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;
                if (!chartArea) return null;
                const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                gradient.addColorStop(0, 'rgba(247, 144, 9, 0.4)');
                gradient.addColorStop(1, 'rgba(247, 144, 9, 0)');
                return gradient;
              },
              tension: 0.4,
              pointRadius: 0,
              borderWidth: 2,
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error fetching penalty revenue data:', error);
    } finally {
      setIsLoading(false);
    }
  };
  /**
   * Effect to reload data when filters change.
   */
  useEffect(() => {
    fetchPenaltyRevenue();
  }, [filters, dateDuration]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
        align: 'end' as const,
        labels: {
          usePointStyle: true,
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 10,
          padding: 15,
        },
      },
      tooltip: {
        /**
         * Function to create tooltip bg color
         * @param context
         * @returns
         */
        backgroundColor: (context: any) => {
          if (context?.tooltipItems?.[0]?.dataset) {
            if (context.tooltipItems[0].dataset.label === t('penaltyRevenue')) {
              return 'rgba(247, 144, 9, 0.9)';
            }
          }
          return 'rgba(0, 0, 0, 0.8)';
        },
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        borderRadius: 8,
        cornerRadius: 4,
        displayColors: false,
        callbacks: {
          /**
           * Function to generate title for chart tooltip
           * @param context
           * @returns
           */
          title: (context: any) => {
            const datasetLabel = context[0]?.dataset?.label;
            return datasetLabel;
          },
          /**
           * Function to show tooltip data
           * @param context
           * @returns
           */
          label: (context: any) => {
            const datasetLabel = context?.dataset?.label;
            return `${context?.raw.toLocaleString()}  ${datasetLabel === t('penaltyRevenue') ? branding.text.currency : 'SAR'}`;
          },
        },
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: true,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          display: true,
        },
        border: {
          display: true,
        },
      },
    },
  };

  return (
    <div className="penalty-revenue-chart-wrap">
      <div className="penalty-revenue-chart-header">
        <h6>{t('penaltyRevenue')}</h6>
        <NavigateArrow className="header-arrow" onClick={handleNavigate} />
      </div>
      <div className="penalty-revenue-chart-container">
        {isLoading ? (
          <div className="battery-loader-wrap">
            <BatteryLoader />
          </div>
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
}

PenaltyRevenueChart.defaultProps = {
  filters: {
    facilityIds: [],
    stationIds: [],
    deviceIds: [],
    type: ['commercial'],
  },
};

export default PenaltyRevenueChart;
